/**
 * Account Section - Settings
 * Sync Status, Billing, and Activity logs
 */

import { useState, useEffect, useRef } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  IconDownload, IconRefresh, IconChevronLeft, IconChevronRight, IconChevronDown, IconChevronUp,
  IconRotate, IconCreditCard, IconCircleCheck, IconExternalLink, IconClock, IconLoader2
} from '@tabler/icons-react';
import { toast } from 'sonner';
import { PricingTable } from '@/components/pricing-table';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { getSessionHeaders } from '@/lib/cloudflare/auth-client';
import { startTrialCheckout } from '@/lib/cloudflare/billing-client';
import { useTriggerSync } from '@/hooks/useTriggerSync';
import {
  fetchLogs,
  fetchLogStats,
  exportLogs,
  formatEventType,
  getEventIcon,
  parseLogTimestamp,
  type LogEntry,
  type LogType,
  type LogStats,
} from '@/lib/cloudflare/logs-client';
import { isActiveSubscription, type Subscription } from '@/types/subscription';



declare global {
  interface Window {
    dataLayer: any[];
  }
}


const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const PAGE_SIZE = 50;

type AccountTab = 'sync' | 'billing' | 'activity';

interface AccountSectionProps {
  billingSuccess?: boolean;
  billingCanceled?: boolean;
  autoStartTrial?: boolean;
  billingRequired?: boolean;
  checkoutPlan?: 'pro' | 'business';
  checkoutInterval?: 'monthly' | 'yearly';
}

export function AccountSection({
  billingSuccess,
  billingCanceled,
  autoStartTrial,
  billingRequired,
  checkoutPlan,
  checkoutInterval,
}: AccountSectionProps) {
  // Default to billing tab if coming from Stripe callback
  const [activeTab, setActiveTab] = useState<AccountTab>(
    billingSuccess || billingCanceled || autoStartTrial || billingRequired ? 'billing' : 'sync'
  );

  const availableTabs: AccountTab[] = billingRequired
    ? ['billing']
    : ['sync', 'billing', 'activity'];

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-border/30 mb-4">
        {availableTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`text-xs pb-2 border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-foreground text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'sync' ? 'Sync Status' : tab === 'billing' ? 'Billing' : 'Activity Log'}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'sync' && <SyncStatusContent />}
      {activeTab === 'billing' && (
        <BillingContent
          success={billingSuccess}
          canceled={billingCanceled}
          autoStartTrial={autoStartTrial}
          billingRequired={billingRequired}
          checkoutPlan={checkoutPlan}
          checkoutInterval={checkoutInterval}
        />
      )}
      {activeTab === 'activity' && <ActivityContent />}
    </div>
  );
}

// Sync Status Content
function SyncStatusContent() {
  const { user } = useCFAuth();
  const { triggerSync, isStarting, isSyncing } = useTriggerSync();
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  useEffect(() => {
    if (user?.lastCleanedAt) {
      setLastSyncTime(new Date(user.lastCleanedAt * 1000));
    }

    // Get last sync time from system logs (more reliable than local state)
    const fetchLastSync = async () => {
      try {
        const data = await fetchLogs({ logType: 'system', limit: 20 });
        const syncLog = data.logs.find(log => log.event_type === 'sync.completed')
          || data.logs.find(log => log.event_type.startsWith('sync.'));
        if (syncLog) {
          const syncDate = parseLogTimestamp(syncLog.created_at);
          // Only update if valid and more recent
          if (!isNaN(syncDate.getTime())) {
            setLastSyncTime((current) => {
              if (!current || syncDate > current) {
                return syncDate;
              }
              return current;
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch last sync:', err);
      }
    };
    fetchLastSync();
  }, [user?.lastCleanedAt]);

  const handleResync = async () => {
    const result = await triggerSync({
      onSuccess: () => {
        toast.success('Sync started! Check the progress in the header.');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to start sync');
      },
    });

    if (result.noEmails) {
      toast.info('No emails found to sync');
    }
  };

  const isDisabled = isStarting || isSyncing;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-4 px-4 bg-muted/30 rounded-lg">
        <div>
          <div className="text-xs font-medium flex items-center gap-2">
            <IconClock className="h-3 w-3" />
            Last Synced
          </div>
          <div className="text-sm mt-1">
            {lastSyncTime ? (
              <span title={format(lastSyncTime, 'PPpp')}>
                {formatDistanceToNow(lastSyncTime, { addSuffix: true })}
              </span>
            ) : (
              <span className="text-muted-foreground">Never synced</span>
            )}
          </div>
        </div>
        <button
          onClick={handleResync}
          disabled={isDisabled}
          className="text-xs px-3 py-1.5 bg-foreground text-background rounded hover:bg-foreground/90 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isStarting ? (
            <IconLoader2 className="h-3 w-3 animate-spin" />
          ) : (
            <IconRotate className="h-3 w-3" />
          )}
          {isSyncing ? 'Syncing...' : isStarting ? 'Starting...' : 'Re-sync Now'}
        </button>
      </div>

      <div className="text-xs text-muted-foreground">
        {isSyncing ? (
          'Sync in progress. Check the progress indicator in the header.'
        ) : (
          'Re-syncing will fetch all emails from Gmail and update categories, priorities, and labels. This may take a few minutes depending on your inbox size.'
        )}
      </div>
    </div>
  );
}

// Billing Content
function BillingContent({
  success,
  canceled,
  autoStartTrial,
  billingRequired,
  checkoutPlan,
  checkoutInterval,
}: {
  success?: boolean;
  canceled?: boolean;
  autoStartTrial?: boolean;
  billingRequired?: boolean;
  checkoutPlan?: 'pro' | 'business';
  checkoutInterval?: 'monthly' | 'yearly';
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [hasLoadedSubscription, setHasLoadedSubscription] = useState(false);
  const autoStartAttemptedRef = useRef(false);

  // Show toast messages for Stripe callback
  const trackingFiredRef = useRef(false);
  
  useEffect(() => {
    if (success) {
      toast.success('Your 30-day free trial is active.');
      
      // Fire GTM Conversion Event exactly once
      if (typeof window !== 'undefined' && window.dataLayer && !trackingFiredRef.current) {
        trackingFiredRef.current = true;
        
        let price = 9.00;
        if (checkoutPlan === 'business') {
          price = checkoutInterval === 'yearly' ? 190.00 : 19.00;
        } else {
          price = checkoutInterval === 'yearly' ? 90.00 : 9.00;
        }

        const storedUtms = localStorage.getItem('wb_utms');
        const utmProps = (() => { try { return storedUtms ? JSON.parse(storedUtms) : {}; } catch { return {}; } })();

        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            currency: 'USD',
            value: price,
            items: [{
              item_name: checkoutPlan === 'business' ? 'Business Plan' : 'Pro Plan',
              price: price,
              quantity: 1
            }]
          },
          ...utmProps
        });
      }

    } else if (canceled) {
      toast.info('Payment canceled. Feel free to try again when ready.');
    }
  }, [success, canceled, checkoutPlan, checkoutInterval]);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await fetch(`${CF_WORKER_URL}/api/subscription`, {
          credentials: 'include',
          headers: getSessionHeaders(),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch subscription status');
        }
        const data = await response.json();
        setSubscription((data?.data?.subscription || data?.subscription || null) as Subscription | null);
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setHasLoadedSubscription(true);
      }
    };

    checkSubscription();
    const interval = setInterval(checkSubscription, 5000);
    return () => clearInterval(interval);
  }, []);

  const handlePortalAccess = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${CF_WORKER_URL}/api/stripe/create-portal`, {
        method: 'POST',
        credentials: 'include',
        headers: getSessionHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData?.error?.message ||
          errorData?.error ||
          'Failed to access billing portal';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const portalUrl = data?.data?.url || data?.url;
      if (!portalUrl) {
        throw new Error('Billing portal did not return a URL');
      }

      const parsedUrl = new URL(portalUrl);
      if (!parsedUrl.hostname.endsWith('.stripe.com')) {
        throw new Error('Invalid billing portal URL');
      }
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to access billing portal:', error);
      toast.error(error instanceof Error ? error.message : 'Could not access billing portal');
      setIsLoading(false);
    }
  };

  const isSubscribed = Boolean(subscription?.plan) && isActiveSubscription(subscription);
  const isTrialing = subscription?.status === 'trialing';
  const trialEndsLabel = subscription?.trialEndsAt
    ? format(new Date(subscription.trialEndsAt * 1000), 'PPP')
    : null;

  const getPlanName = () => {
    if (!subscription?.plan) return billingRequired ? 'Trial required' : 'No active plan';
    return subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1);
  };

  const getIntervalLabel = () => {
    if (!subscription?.interval) return '';
    return subscription.interval === 'monthly' ? '/mo' : '/yr';
  };

  useEffect(() => {
    if (!autoStartTrial || autoStartAttemptedRef.current || !hasLoadedSubscription) {
      return;
    }

    if (isSubscribed) {
      autoStartAttemptedRef.current = true;
      return;
    }

    autoStartAttemptedRef.current = true;
    setIsLoading(true);

    startTrialCheckout({
      plan: checkoutPlan || 'pro',
      interval: checkoutInterval || 'yearly',
    })
      .then((checkoutUrl) => {
        window.location.href = checkoutUrl;
      })
      .catch((error) => {
        console.error('Failed to auto-start checkout:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
        setIsLoading(false);
      });
  }, [autoStartTrial, checkoutPlan, checkoutInterval, hasLoadedSubscription, isSubscribed]);

  useEffect(() => {
    if (!success || !isSubscribed) return;

    const redirectTimeout = window.setTimeout(() => {
      window.location.replace('/');
    }, 1200);

    return () => window.clearTimeout(redirectTimeout);
  }, [success, isSubscribed]);

  return (
    <div className="space-y-4">
      {billingRequired && !isSubscribed && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Start your 30-day trial to unlock inbox cleanup, sync, rules, and the rest of the app.
        </div>
      )}

      {/* Current Plan */}
      <div className="flex items-center justify-between py-4 px-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-4">
          <IconCreditCard className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-xs font-medium">Current Plan</div>
            <div className="text-sm flex items-center gap-2">
              <span className="font-mono">{getPlanName()}</span>
              {getIntervalLabel() && <span className="text-muted-foreground">{getIntervalLabel()}</span>}
              <span className={`w-1.5 h-1.5 rounded-full ${isSubscribed ? 'bg-green-500' : 'bg-muted-foreground'}`} />
              <span className="text-xs text-muted-foreground">
                {isTrialing ? 'Trial' : isSubscribed ? 'Active' : billingRequired ? 'Setup needed' : 'Inactive'}
              </span>
            </div>
            {isTrialing && trialEndsLabel && (
              <div className="text-xs text-muted-foreground mt-1">
                Trial ends {trialEndsLabel}
              </div>
            )}
          </div>
        </div>
        {isSubscribed && (
          <button
            onClick={handlePortalAccess}
            disabled={isLoading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <IconExternalLink className="h-3 w-3" />
            {isLoading ? 'Loading...' : 'Manage Billing'}
          </button>
        )}
      </div>

      {/* Pricing Table */}
      <PricingTable subscription={subscription} isPublicPage={false} />
    </div>
  );
}

// Activity Content
function ActivityContent() {
  const [activeLogTab, setActiveLogTab] = useState<'all' | LogType>('all');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const loadLogs = async (logType?: LogType, pageNum = 0) => {
    setIsLoading(true);
    try {
      const data = await fetchLogs({
        logType,
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
      });
      setLogs(data.logs);
      setTotal(data.pagination?.total || data.total || 0);
    } catch (error) {
      console.error('Failed to load logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fetchLogStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadLogs(activeLogTab === 'all' ? undefined : activeLogTab, page);
  }, [activeLogTab, page]);

  useEffect(() => {
    loadStats();
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportLogs({
        logType: activeLogTab === 'all' ? undefined : activeLogTab,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Logs exported successfully');
    } catch (error) {
      console.error('Failed to export logs:', error);
      toast.error('Failed to export logs');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRefresh = () => {
    loadLogs(activeLogTab === 'all' ? undefined : activeLogTab, page);
    loadStats();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Calculate summary stats
  const loginCount = stats?.byEventType.filter(s => s.event_type === 'auth.login').reduce((acc, s) => acc + s.count, 0) || 0;
  const actionCount = stats?.byEventType.filter(s => s.log_type === 'action').reduce((acc, s) => acc + s.count, 0) || 0;
  const syncCount = stats?.byEventType.filter(s => s.event_type.startsWith('sync.')).reduce((acc, s) => acc + s.count, 0) || 0;

  return (
    <div>
      {/* Stats header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span><span className="font-mono text-foreground">{loginCount}</span> sign-ins</span>
          <span><span className="font-mono text-foreground">{actionCount}</span> actions</span>
          <span><span className="font-mono text-foreground">{syncCount}</span> syncs</span>
          <span className="text-muted-foreground/50">last 30 days</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <IconRefresh className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <IconDownload className="h-3 w-3" />
            Export
          </button>
        </div>
      </div>

      {/* Log type tabs */}
      <div className="flex items-center gap-4 border-b border-border/30 mb-0">
        {(['all', 'audit', 'action', 'system'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveLogTab(tab); setPage(0); }}
            className={`text-xs pb-2 border-b-2 transition-colors ${
              activeLogTab === tab
                ? 'border-foreground text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'all' ? 'All' : tab === 'audit' ? 'Security' : tab === 'action' ? 'Actions' : 'System'}
          </button>
        ))}
      </div>

      {/* Log Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <IconRefresh className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8 text-xs text-muted-foreground">
          No activity logs found
        </div>
      ) : (
        <div className="divide-y divide-border/20">
          {logs.map((log) => (
            <LogItem key={log.id} log={log} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between py-3 border-t border-border/30 mt-2">
          <div className="text-xs text-muted-foreground">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <IconChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <IconChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCategory(category: string): string {
  const labels: Record<string, string> = {
    do_nothing: 'Inbox',
    delete: 'Delete',
    archive: 'Archive',
    unsubscribe: 'Unsubscribe',
  };
  return labels[category] || category;
}

function LogItem({ log }: { log: LogEntry }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const details = log.details ? JSON.parse(log.details) : null;
  const createdAt = parseLogTimestamp(log.created_at);
  const emails = details?.emails as Array<{ subject: string; sender: string; category: string }> | undefined;
  const hasEmails = emails && emails.length > 0;

  return (
    <div className="py-1.5 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
      <div className="flex items-center gap-3 text-xs">
        {/* Icon */}
        <span className="text-sm w-5 text-center shrink-0">{getEventIcon(log.event_type)}</span>

        {/* Event type */}
        <span className="font-medium w-32 shrink-0 truncate">{formatEventType(log.event_type)}</span>

        {/* Details */}
        <span className="flex-1 text-muted-foreground truncate">
          {details?.email && <span>{details.email}</span>}
          {details?.successCount !== undefined && (
            <span className="font-mono">{details.successCount} ok</span>
          )}
          {details?.errorCount > 0 && (
            <span className="text-destructive ml-2 font-mono">{details.errorCount} failed</span>
          )}
          {log.affected_count > 1 && !details?.successCount && (
            <span className="font-mono">{log.affected_count} items</span>
          )}
        </span>

        {/* Status */}
        {log.status === 'failure' && (
          <span className="text-destructive text-[10px] font-medium shrink-0">FAILED</span>
        )}

        {/* Duration */}
        {log.duration_ms && (
          <span className="text-muted-foreground/60 font-mono w-14 text-right shrink-0">{log.duration_ms}ms</span>
        )}

        {/* Time */}
        <span
          className="text-muted-foreground w-20 text-right shrink-0"
          title={format(createdAt, 'PPpp')}
        >
          {formatDistanceToNow(createdAt, { addSuffix: false })}
        </span>

        {/* Expand button */}
        {hasEmails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            {isExpanded ? <IconChevronUp className="h-3 w-3" /> : <IconChevronDown className="h-3 w-3" />}
          </button>
        )}
      </div>

      {/* Error message */}
      {log.error_message && (
        <div className="text-[11px] text-destructive mt-1 ml-8 truncate">{log.error_message}</div>
      )}

      {/* Expandable email details */}
      {hasEmails && isExpanded && (
        <div className="mt-1 ml-8 border-l border-border/30 pl-3">
          <table className="w-full text-[11px]">
            <tbody>
              {emails.map((email, index) => (
                <tr key={index} className="hover:bg-black/[0.01] dark:hover:bg-white/[0.01]">
                  <td className="py-0.5 pr-3 text-muted-foreground w-40 truncate max-w-[160px]" title={email.sender}>
                    {email.sender.split('@')[0]}
                  </td>
                  <td className="py-0.5 pr-3 truncate" title={email.subject}>
                    {email.subject}
                  </td>
                  <td className="py-0.5 text-right text-muted-foreground w-20">
                    {email.category && formatCategory(email.category)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {emails.length >= 50 && (
            <div className="text-[10px] text-muted-foreground/60 mt-1">
              +more...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
